/**
 * Die Zuweisungsfelder einer Aufgabe - Portierung des mittleren Teils von `renderTaskDetailPanel`
 * fuer den Aufgaben-Fall (`index.html:9078-9082`): Zuständigkeitsmodul, Hauptverantwortlich und (nur
 * bei zugewiesenem Modul) Vertretung.
 *
 * Fragment OHNE eigene Huelle - alle Kinder haengen FLACH unter `.task-detail-properties`
 * (`AufgabenDetail.tsx`), gemeinsam mit dem Faelligkeitsdatum, das dort selbst steht (`:9083-9084`).
 *
 * ================= UMGESTELLT AM 2026-08-15: EIN FELD STATT VIER =================
 *
 * Bis hierher rief diese Komponente `applyResponsibilityToTask` (`lib/payments.ts:573`, Port von
 * `index.html:8848`) und reichte die fertig veraenderte Aufgabenkopie nach oben. Jene Funktion setzt
 * VIER Felder: `responsibilityId`, `responsibilityName`, `assignee` und `deputyAssignee` - im
 * Bestand richtig, weil die Aufgabe ein freies JSON-Objekt war und Verantwortliche dort NAMEN.
 *
 * Im neuen Modell traegt `order_tasks` nur `responsibility_id`; `assignee`/`deputy_assignee` sind
 * Fremdschluessel auf `users` und werden vom SERVER gesetzt: `PATCH /api/responsibility-modules/
 * :moduleId` uebertraegt Hauptverantwortung und Vertretung in derselben Transaktion auf alle offenen
 * Tasks des Moduls (`backend/src/domaene/zustaendigkeit.ts#uebertrageVerantwortung`). Einen Namen
 * dorthin zu schreiben ergaebe SQLSTATE 22P02, also einen 500.
 *
 * Diese Komponente meldet deshalb nur noch die gewaehlte MODUL-ID. `applyResponsibilityToTask` wird
 * hier nicht mehr gerufen - sie bleibt fuer `ensurePaymentRequestTask` (`lib/payments.ts`) bestehen,
 * das Aufgaben weiterhin in der Bestandsform erzeugt.
 *
 * ================= WAS DIE BEIDEN NAMENSFELDER JETZT ZEIGEN =================
 *
 * Sie kommen aus dem MODUL, nicht mehr aus der Aufgabe: `order_tasks.assignee` traegt eine uuid, die
 * die SPA nicht aufloesen kann (sie laedt keine Nutzerliste - die gibt es nur hinter `manage_users`).
 * `responsibility_modules` liefert `ownerUserId`/`deputyUserId`, also ebenfalls uuids.
 *
 * Solange es keinen Leseweg fuer Nutzer gibt, stehen die beiden Felder deshalb LEER statt eine
 * Kennung zu zeigen, die niemandem etwas sagt - mit einem Hinweis im `title`. Der Bestand zeigte hier
 * Namen; das ist ein benannter Verlust dieser Runde und keine Absicht des Zielmodells. Wer ihn
 * schliessen will, braucht `GET /api/users` in der SPA - dann kommen beide Namen aus dem Modul.
 *
 * ================= Rechte: dieses Feld prueft KEINE =================
 *
 * `manage_tasks` kommt in `index.html` genau EINMAL vor - als Eintrag in
 * `DRAWER_TAB_EDIT_PERMISSIONS` (`:8366`). `renderTaskDetailPanel` selbst prueft nirgends
 * `hasPermission`. Siehe Kopfkommentar von `AufgabenDetail.tsx` fuer die volle Begruendung, warum das
 * Select hier IMMER bedienbar bleibt, unabhaengig vom Rechtesatz - ein Befund am Bestand, nicht
 * dieser Portierung.
 */
import { feldZustandClass } from "../../../utils/feldZustand.ts";
import type { Zustaendigkeitsmodul } from "../../../api/zustaendigkeitsModule.ts";
import type { OrderTask } from "../../../lib/types.ts";

/**
 * Tooltip fuer Hauptverantwortlich/Vertretung - beide sind strukturell gesperrt (kein Schreibkanal,
 * siehe Kopfkommentar), nicht rollenbasiert. Woertlich aus `feldzustand-spec.md`, Tabelle "Gesperrt",
 * exakt uebernommen - keine eigene Formulierung. Warum die Felder ZUSAETZLICH leer bleiben (statt
 * eine Nutzerkennung zu zeigen), steht weiterhin im Kopfkommentar ("WAS DIE BEIDEN NAMENSFELDER JETZT
 * ZEIGEN").
 */
const STRUKTURELL_GESPERRT_TITEL = "Aktuell ohne Schreibweg, nur zur Anzeige.";

export interface ZuweisungProps {
  aufgabe: OrderTask;
  /** `GET /api/responsibility-modules` - die echten Module, nicht die Vorlagenform des Bestands. */
  module: readonly Zustaendigkeitsmodul[];
  /** Meldet die gewaehlte Modul-Id (leer = kein Modul). Siehe Kopfkommentar. */
  onZuweisen: (modulId: string) => void;
}

export function Zuweisung({ aufgabe, module, onZuweisen }: ZuweisungProps) {
  const responsibilityId = (aufgabe.responsibilityId as string) || "";
  const modul = module.find((m) => m.id === responsibilityId) ?? null;

  return (
    <>
      <label>Zuständigkeitsmodul</label>
      {/* `responsibilityModuleOptions` (index.html:8842-8846) - der leere Eintrag zuerst. */}
      <select value={responsibilityId} onChange={(e) => onZuweisen(e.currentTarget.value)}>
        <option value="">Kein Modul / direkte Zuweisung</option>
        {module.map((m) => (
          <option key={m.id} value={m.id}>
            {m.name}
          </option>
        ))}
      </select>
      <label>Hauptverantwortlich</label>
      {/*
        IMMER `readOnly`, anders als im Bestand: dort war das Feld ohne Modul frei beschreibbar
        (`patchOpenTaskDetail('assignee', …)`, index.html:9081). Der Wert waere heute eine
        Nutzerkennung - ein getippter Name traefe die uuid-Spalte und ergaebe einen 500. Siehe
        `api/aufgaben.ts#KEIN_AUFGABEN_WEG`.
      */}
      {/*
        NACHBESSERUNG (Icon-Position + Grid-Verschiebung, Pruefer-Befund, erste Nachbesserung):
        `<input>` und Schloss-Icon lagen ohne Wrapper FLACH als eigene Kinder unter
        `.task-detail-properties` (`AufgabenDetail.tsx`, `grid-template-columns: 135px
        minmax(0,1fr)`) - jedes direkte Kind belegt dort eine eigene Gitterzelle, das Icon verschob
        dadurch alle nachfolgenden Label/Feld-Paare um eine Spalte.

        NACHBESSERUNG 2 (Auftrag 7 Punkt 2, Icon-Anker vereinheitlicht): das Schloss-Icon sass danach
        UEBERALL (14 JSX-Stellen + der imperative Rechte-Durchgang in `Drawer.tsx`) in einem eigenen,
        zero-size `.feld-lock-anchor`-Wrapper direkt um sich herum - ein zero-size Wrapper allein
        haette hier aber WEITERHIN eine eigene Gitterzelle belegt (jedes direkte Grid-Kind tut das,
        unabhaengig von seiner Groesse). `.feld-lock-zelle` fasste deshalb `<input>` + den (darin
        zero-size) `.feld-lock-anchor` zu GENAU EINER Gitterzelle zusammen.

        NACHBESSERUNG 3 (Auftrag 8, strukturell statt Sibling-Element): kein Schloss-Icon-Element
        mehr im DOM - das Symbol kommt seither als CSS `background-image` direkt ueber
        `.feld-gesperrt` (`styles/neubau/feldzustand.css`), gesetzt am `<input>` selbst. Ohne
        zweites Geschwister-Element braucht es die `.feld-lock-zelle`-Klammer nicht mehr: das
        `<input>` steht wieder ALLEIN als direktes Grid-Kind, genau wie vor der Feldzustand-Optik -
        derselbe strukturelle Fix, den dieser Auftrag fuer `.task-phase-head`/`.task-detail-
        properties` generell verlangt (kein Sonderfall je Layout-Kontext mehr noetig).
      */}
      <input
        value=""
        placeholder={
          modul?.ownerUserId
            ? "Über das Zuständigkeitsmodul gesetzt"
            : "Keine verantwortliche Person"
        }
        readOnly
        aria-readonly="true"
        className={feldZustandClass({ gesperrt: true })}
        title={STRUKTURELL_GESPERRT_TITEL}
        onChange={() => {}}
      />
      {responsibilityId ? (
        <>
          <label>Vertretung</label>
          <input
            value=""
            placeholder={
              modul?.deputyUserId ? "Über das Zuständigkeitsmodul gesetzt" : "Keine Vertretung"
            }
            readOnly
            aria-readonly="true"
            className={feldZustandClass({ gesperrt: true })}
            title={STRUKTURELL_GESPERRT_TITEL}
            onChange={() => {}}
          />
        </>
      ) : null}
    </>
  );
}
